import { Alert, Button, Checkbox, Input, Spin, Tag } from "@arco-design/web-react";
import { SendHorizontal } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";

import { useCurrentAuthUser } from "../../auth";
import {
  useCustomizationMobileWorkbenchSession,
} from "..";
import {
  customizationRoleLabels,
  customizationSourceLabels,
  type CustomizationReferenceDemandCandidate,
  type CustomizationRouteLinkRecognitionResult,
  type CustomizationTraceItem,
} from "../../customization";
import { canUsePermission } from "../../../lib/auth";

/** 手机端需求追溯独立页面。 */
export function CustomizationMobileTracePage() {
  const currentUserQuery = useCurrentAuthUser();
  const canAddTrace = canUsePermission(currentUserQuery.data, "customization.project:trace");
  const {
    confirmTraceSelection,
    confirmedTraceIds,
    expandedTraceIds,
    pendingTraceIds,
    runtime: mobileRuntime,
    toggleExpandedTrace,
    togglePendingTrace,
  } = useCustomizationMobileWorkbenchSession();
  const runtime = mobileRuntime.sharedRuntime;
  const [traceContent, setTraceContent] = useState("");
  const [localNotice, setLocalNotice] = useState("");
  const previewRequestRef = useRef(0);

  const handleTraceContentChange = (content: string) => {
    setTraceContent(content);
    const requestId = ++previewRequestRef.current;
    window.setTimeout(() => {
      if (previewRequestRef.current === requestId) {
        void runtime.previewTraceInput(content);
      }
    }, 0);
  };

  const handleSubmitTrace = async () => {
    const content = traceContent.trim();
    if (!content) {
      return;
    }
    setLocalNotice("");
    await runtime.submitTrace({
      content,
      isImportant: false,
      role: "sales",
      sourceType: "backend",
    });
    setTraceContent("");
    void runtime.previewTraceInput("");
  };

  if (runtime.loading) {
    return (
      <main className="customization-mobile-workbench-page customization-mobile-trace-page" aria-label="手机需求追溯页">
        <div className="customization-mobile-loading">
          <Spin dot />
          <span>加载中...</span>
        </div>
      </main>
    );
  }

  return (
    <main className="customization-mobile-workbench-page customization-mobile-trace-page" aria-label="手机需求追溯页">
      <span className="customization-mobile-visually-hidden">
        {runtime.project?.name?.trim() || "需求追溯"}
      </span>
      <span className="customization-mobile-visually-hidden">
        {runtime.displayDemand?.versionNumber ?? "未生成"}
      </span>

      <div className="customization-mobile-trace-scroll customization-mobile-page-scroll">

      {runtime.errorMessage ? (
        <Alert className="customization-mobile-alert" type="error" content={runtime.errorMessage} showIcon />
      ) : null}
      {runtime.mutationErrorMessage ? (
        <Alert className="customization-mobile-alert" type="error" content={runtime.mutationErrorMessage} showIcon />
      ) : null}
      {localNotice ? (
        <Alert className="customization-mobile-alert" type="error" content={localNotice} showIcon />
      ) : null}

      <section className="customization-mobile-trace-input" aria-label="手机追溯输入区">
        <div className="customization-mobile-trace-section-head">
          <strong>新增追溯</strong>
          <Tag color="arcoblue">AI 识别</Tag>
        </div>
        <Input.TextArea
          aria-label="需求追溯内容"
          disabled={!canAddTrace}
          rows={5}
          placeholder="输入需求、JD号或旧系统行程链接"
          value={traceContent}
          onChange={handleTraceContentChange}
        />
        <div className="customization-mobile-trace-submit-row">
          <Button
            size="small"
            type="primary"
            loading={runtime.traceSubmitting}
            disabled={!canAddTrace || runtime.traceSubmitting}
            icon={<SendHorizontal size={16} />}
            onClick={handleSubmitTrace}
          >
            发送记录
          </Button>
        </div>
        <MobileReferenceDemandCandidates
          candidates={runtime.referenceCandidates}
          selectedDemandId={runtime.selectedReferenceDemandId}
          onToggle={runtime.toggleReferenceDemand}
        />
        <MobileRouteLinkPreviewCard
          loading={runtime.routeLinkPreviewing}
          preview={runtime.routeLinkPreview}
        />
      </section>

      <section className="customization-mobile-trace-list" aria-label="手机追溯记录列表">
        <div className="customization-mobile-trace-section-head">
          <strong>追溯记录</strong>
          <span>{runtime.traces.length} 条</span>
        </div>
        {runtime.traces.length > 0 ? (
          runtime.traces.map((trace) => (
            <MobileTraceItem
              key={trace.id}
              trace={trace}
              selected={pendingTraceIds.includes(trace.id)}
              expanded={expandedTraceIds.includes(trace.id)}
              referenceCandidates={runtime.traceReferenceCandidatesByTraceId[trace.id] ?? []}
              routePreview={runtime.traceRouteLinkPreviewByTraceId[trace.id] ?? null}
              onToggle={() => togglePendingTrace(trace.id)}
              onToggleExpanded={() => toggleExpandedTrace(trace.id)}
            />
          ))
        ) : (
          <div className="customization-mobile-empty">
            <strong>暂无追溯记录</strong>
            <span>新增沟通记录后，可勾选用于生成需求版本。</span>
          </div>
        )}
      </section>

      </div>

      <section className="customization-mobile-trace-generate-bar" aria-label="手机追溯确认操作区">
        <div>
          <strong>
            {pendingTraceIds.length > 0
              ? `已选 ${pendingTraceIds.length} 条追溯`
              : confirmedTraceIds.length > 0
                ? "已取消全部追溯"
                : "请选择追溯记录"}
          </strong>
          <span>确定后带回需求定制</span>
        </div>
        <Button
          className="customization-mobile-trace-confirm-button"
          disabled={pendingTraceIds.length === 0 && confirmedTraceIds.length === 0}
          type="primary"
          onClick={confirmTraceSelection}
        >
          确定
        </Button>
      </section>
    </main>
  );
}

/** 渲染手机端参考定制候选列表。 */
function MobileReferenceDemandCandidates({
  candidates,
  selectedDemandId,
  onToggle,
}: {
  candidates: CustomizationReferenceDemandCandidate[];
  selectedDemandId?: string | null;
  onToggle: (demandId: string) => void;
}) {
  if (candidates.length === 0) {
    return null;
  }

  return (
    <div className="customization-mobile-reference-list" aria-label="参考定制候选">
      {candidates.map((candidate) => (
        <MobileDemandReferenceCard
          key={candidate.demandId}
          candidate={candidate}
          selected={selectedDemandId === candidate.demandId}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

/** 渲染手机端需求定制参考卡片。 */
function MobileDemandReferenceCard({
  candidate,
  selected = false,
  onToggle,
}: {
  candidate: CustomizationReferenceDemandCandidate;
  selected?: boolean;
  onToggle?: (demandId: string) => void;
}) {
  return (
    <article className={`customization-mobile-preview-card${selected ? " is-selected" : ""}`} aria-label="需求定制预览">
      {onToggle ? (
        <Checkbox
          checked={selected}
          aria-label={`选择参考定制 ${candidate.demandId}`}
          onChange={() => onToggle(candidate.demandId)}
        />
      ) : null}
      <div>
        <div className="customization-mobile-preview-card-head">
          <span>需求定制预览</span>
          <Tag color={selected ? "green" : "arcoblue"}>{candidate.referenceKey}</Tag>
        </div>
        <strong>{candidate.title}</strong>
        <ul>
          {candidate.highlights.map((highlight) => (
            <li key={highlight}>{highlight}</li>
          ))}
        </ul>
      </div>
    </article>
  );
}

/** 渲染手机端旧系统行程链接预览。 */
function MobileRouteLinkPreviewCard({
  preview,
  loading,
}: {
  preview: CustomizationRouteLinkRecognitionResult | null;
  loading?: boolean;
}) {
  if (!preview?.route && !loading) {
    return null;
  }
  const route = preview?.route;
  const highlights = route ? buildMobileRoutePreviewHighlights(route) : [];
  return (
    <article className="customization-mobile-preview-card customization-mobile-route-preview" aria-label="行程预览">
      <div>
        <div className="customization-mobile-preview-card-head">
          <span>行程预览</span>
          <Tag color="green">行程</Tag>
        </div>
        <strong>{route?.title ?? "行程预览加载中"}</strong>
        {highlights.length > 0 ? (
          <ul>
            {highlights.map((highlight) => (
              <li key={highlight}>{highlight}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </article>
  );
}

/** 渲染手机端单条追溯记录。 */
function MobileTraceItem({
  trace,
  referenceCandidates,
  routePreview,
  selected,
  expanded,
  onToggle,
  onToggleExpanded,
}: {
  trace: CustomizationTraceItem;
  referenceCandidates: CustomizationReferenceDemandCandidate[];
  routePreview: CustomizationRouteLinkRecognitionResult | null;
  selected: boolean;
  expanded: boolean;
  onToggle: () => void;
  onToggleExpanded: () => void;
}) {
  const primaryReference = referenceCandidates[0];
  const isSelectable = trace.isSelectable !== false;
  const contentRef = useRef<HTMLParagraphElement>(null);
  const [canExpand, setCanExpand] = useState(false);

  useLayoutEffect(() => {
    if (expanded || !contentRef.current) {
      return;
    }
    const content = contentRef.current;
    const measureOverflow = () => {
      setCanExpand(content.scrollHeight > content.clientHeight + 1);
    };
    measureOverflow();
    const observer = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(measureOverflow);
    observer?.observe(content);
    window.addEventListener("resize", measureOverflow);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measureOverflow);
    };
  }, [expanded, trace.content]);

  return (
    <article className={`customization-mobile-trace-item${selected ? " is-selected" : ""}`}>
      <Checkbox
        checked={selected}
        disabled={!isSelectable}
        aria-label={isSelectable ? `选择追溯记录 ${trace.id}` : `只读追溯来源 ${trace.id}`}
        onChange={onToggle}
      />
      <div className="customization-mobile-trace-card">
        <div className="customization-mobile-trace-card-head">
          <strong>
            {customizationRoleLabels[trace.role] ?? trace.role} · {customizationSourceLabels[trace.sourceType] ?? trace.sourceType}
          </strong>
          <time>{trace.createdAt}</time>
        </div>
        {primaryReference ? (
          <MobileDemandReferenceCard candidate={primaryReference} />
        ) : routePreview ? (
          <MobileRouteLinkPreviewCard preview={routePreview} />
        ) : (
          <div className="customization-mobile-trace-content">
            <p
              ref={contentRef}
              className={`customization-mobile-trace-copy${expanded ? " is-expanded" : ""}`}
            >
              {trace.content}
            </p>
            {canExpand ? (
              <button
                className="customization-mobile-trace-expand"
                type="button"
                aria-expanded={expanded}
                onClick={onToggleExpanded}
              >
                {expanded ? "收起" : "展开全文"}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </article>
  );
}

/** 生成手机端行程预览摘要。 */
function buildMobileRoutePreviewHighlights(
  route: NonNullable<CustomizationRouteLinkRecognitionResult["route"]>,
): string[] {
  const dateRange = [route.startDate, route.endDate].filter(Boolean).join(" ~ ");
  const pointNames = (route.pointNames ?? []).map((item) => item.trim()).filter(Boolean);
  const stayAreaNames = (route.stayAreaNames ?? []).map((item) => item.trim()).filter(Boolean);
  return [
    dateRange ? `日期：${dateRange}` : "",
    route.dispatcherUserName ? `第一计调：${route.dispatcherUserName}` : "",
    pointNames.length > 0 ? `考察点：${pointNames.join("、")}` : "",
    stayAreaNames.length > 0 ? `住宿点：${stayAreaNames.join("、")}` : "",
    route.summary?.trim() ?? "",
  ].filter(Boolean);
}
